// Lightweight inline-SVG chart primitives. Avoids adding a chart library.

export function Sparkline({
  data,
  width = 240,
  height = 60,
  strokeWidth = 2,
  className = '',
}: {
  data: number[]
  width?: number
  height?: number
  strokeWidth?: number
  className?: string
}) {
  if (data.length === 0) {
    return <div className={`text-xs text-neutral-400 ${className}`}>No data</div>
  }
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const range = Math.max(max - min, 1)
  const stepX = data.length > 1 ? width / (data.length - 1) : width
  const points = data
    .map((v, i) => `${i * stepX},${height - ((v - min) / range) * (height - strokeWidth) - strokeWidth / 2}`)
    .join(' ')
  const last = points.split(' ').pop() ?? ''
  const [lastX, lastY] = last.split(',').map(Number)
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className} role="img" aria-label="Trend over time">
      <defs>
        <linearGradient id="sparkline-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#FF8A3D" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#FF8A3D" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${points} ${width},${height}`}
        fill="url(#sparkline-fill)"
      />
      <polyline
        points={points}
        fill="none"
        stroke="#FF8A3D"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {!Number.isNaN(lastX) && !Number.isNaN(lastY) && (
        <circle cx={lastX} cy={lastY} r={3} fill="#0A2240" />
      )}
    </svg>
  )
}

export function BarChart({
  data,
  height = 200,
  className = '',
}: {
  data: Array<{ label: string; value: number }>
  height?: number
  className?: string
}) {
  if (data.length === 0) return <div className={`text-xs text-neutral-400 ${className}`}>No data</div>
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-end gap-2" style={{ height }}>
        {data.map((d) => {
          const h = (d.value / max) * (height - 24)
          return (
            <div key={d.label} className="flex-1 flex flex-col items-center justify-end gap-1.5 min-w-0">
              <div className="text-[10px] font-mono text-neutral-500">{d.value}</div>
              <div
                className="w-full bg-secondary rounded-t"
                style={{ height: `${h}px`, minHeight: '2px' }}
                title={`${d.label}: ${d.value}`}
              />
              <div className="text-[10px] text-neutral-600 truncate w-full text-center" title={d.label}>
                {d.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const DONUT_COLORS = ['#0A2240', '#1E3A6B', '#2EC4C7', '#FF8A3D', '#FF4D6D', '#7C8798']

export function DonutChart({
  data,
  size = 160,
  thickness = 22,
  className = '',
}: {
  data: Array<{ label: string; value: number }>
  size?: number
  thickness?: number
  className?: string
}) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) {
    return (
      <div className={`text-xs text-neutral-400 ${className}`} style={{ width: size, height: size }}>
        No data
      </div>
    )
  }
  const r = (size - thickness) / 2
  const circ = 2 * Math.PI * r

  // Pre-compute the running offset for each slice so we don't mutate
  // outside the map callback.
  const segments = data.reduce<
    Array<{ label: string; value: number; frac: number; offset: number }>
  >((acc, d) => {
    const prev = acc[acc.length - 1]
    const offset = prev ? prev.offset + prev.frac : 0
    acc.push({ label: d.label, value: d.value, frac: d.value / total, offset })
    return acc
  }, [])

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Composition">
        <g transform={`translate(${size / 2}, ${size / 2}) rotate(-90)`}>
          {segments.map((s, i) => (
            <circle
              key={s.label}
              r={r}
              fill="none"
              stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth={thickness}
              strokeDasharray={`${s.frac * circ} ${circ}`}
              strokeDashoffset={-s.offset * circ}
            >
              <title>{`${s.label}: ${s.value} (${Math.round(s.frac * 100)}%)`}</title>
            </circle>
          ))}
        </g>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-primary"
          style={{ fontSize: 22, fontWeight: 700 }}
        >
          {total}
        </text>
      </svg>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
        {data.map((d, i) => (
          <li key={d.label} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
              aria-hidden="true"
            />
            <span className="text-neutral-700">
              {d.label}{' '}
              <span className="text-neutral-400">({d.value})</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
